import React, { useEffect, useMemo, useState } from 'react';
import MyMasonry from '../../components/Mansory/Mansory';
import { getProducts, Products } from '../../hooks/api-methods/get-products';
import { postProduct } from '../../hooks/api-methods/post-product';
import { deleteProduct } from '../../hooks/api-methods/delete-products';
import CustomDropdown from '../../components/Buttons/Dropdown';
import SearchInput from '../../components/Inputs/SearchInput';
import Loader from '../../components/Loader/Loader';
import { useScreenSize } from '../../hooks/useScreenSize';
import { Trash2 } from 'lucide-react';
import ConfirmModal from '../../components/PopUps/ConfirmModal';
import ProductSidebar from './sidebar/SideBarProducts';
import Button from '../../components/Buttons/Button';
import SimpleSlider from '../../components/Slider/PreviewSlider';

/* ---------------------------------- */
/* Constants */
/* ---------------------------------- */
export const EMPTY_PRODUCT: Products = {
  id: '',
  subject: '',
  title: '',
  desc: '',
  location: '',
  duration: 0,
  images: [],
  product: [],
};

/* ---------------------------------- */
/* Component */
/* ---------------------------------- */

const ProductManager: React.FC = () => {
  const { isMobile } = useScreenSize();

  const [products, setProducts] = useState<Products[]>([]);
  const [filteredSubject, setFilteredSubject] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [newProduct, setNewProduct] = useState<Products>(EMPTY_PRODUCT);

  /* ---------------------------------- */
  /* Modal State */
  /* ---------------------------------- */
  const [showModal, setShowModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState<Products | null>(null);

  /* ---------------------------------- */
  /* Fetch products */
  /* ---------------------------------- */

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const data = await getProducts();
        setProducts(data);
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  /* ---------------------------------- */
  /* Derived data */
  /* ---------------------------------- */

  const subjectOptions = useMemo(() => {
    const subjects = products
      .map((p) => p.subject)
      .filter((subject): subject is string => typeof subject === 'string' && subject.length > 0);

    return ['all', ...Array.from(new Set(subjects))];
  }, [products]);

  const filteredProducts = useMemo(() => {
    return products.filter((product) => {
      const matchesSubject = filteredSubject === 'all' || product.subject === filteredSubject;

      const matchesName = product.title.toLowerCase().includes(searchTerm.toLowerCase());

      return matchesSubject && matchesName;
    });
  }, [products, filteredSubject, searchTerm]);

  /* ---------------------------------- */
  /* Handlers */
  /* ---------------------------------- */

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;

    setNewProduct((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAddProduct = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setIsSubmitting(true);

      const createdProduct = await postProduct({
        ...newProduct,
        id: crypto.randomUUID(),
      });

      setProducts((prev) => [createdProduct, ...prev]);
      setNewProduct(EMPTY_PRODUCT);
      setIsSidebarOpen(false);
    } catch (error) {
      console.error('Failed to add product:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const openDeleteModal = (product: Products) => {
    setSelectedFile(product);
    setShowModal(true);
  };

  const confirmDeleteProduct = async () => {
    if (!selectedFile) return;

    try {
      await deleteProduct(selectedFile.id);
      setProducts((prev) => prev.filter((product) => product.id !== selectedFile.id));
    } catch (error) {
      console.error('Failed to delete product:', error);
    } finally {
      setShowModal(false);
      setSelectedFile(null);
    }
  };

  /* ---------------------------------- */
  /* Render */
  /* ---------------------------------- */

  if (loading) return <Loader />;

  return (
    <div className="flex-column gap-2 w-100">
      <h3>Manage Products</h3>
      <div className={`ptb-1 ${isMobile ? 'flex-colomn' : 'flex-row gap-2 space-between'}`}>
        <div className={`ptb-1 ${isMobile ? 'flex-colomn' : 'flex-row-align'}`}>
          <SearchInput
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Search by name..."
            width="300px"
          />
          <CustomDropdown
            options={subjectOptions}
            value={filteredSubject}
            onChange={setFilteredSubject}
            textInput="Filter by subject"
          />
        </div>
        <div className="flex-row-align">
          <Button text="+ Add product" onClick={() => setIsSidebarOpen(true)} />
        </div>
      </div>

      <MyMasonry
        items={filteredProducts}
        renderItem={(product: Products) => (
          <div className="card-product p-1 w-100 relative">
            <SimpleSlider showNavigation={false}>
              {product.images.map((img, index) => (
                <img
                  key={index}
                  className="img-large"
                  src={img}
                  alt={`${product.title} - ${index + 1}`}
                />
              ))}
            </SimpleSlider>

            <h4>
              <strong>Title:</strong> {product.title}
            </h4>

            <p>
              <strong>Subject:</strong> {product.subject}
            </p>

            <p>
              <strong>Description:</strong> {product.desc}
            </p>
            <button
              onClick={() => openDeleteModal(product)}
              className="button-delete"
              title="Delete product"
            >
              <Trash2 size={18} />
            </button>
          </div>
        )}
      />

      {filteredProducts.length === 0 && <p className="w-100 vh-75">No products found.</p>}

      {/* Overlay */}
      {isSidebarOpen && <div className="sidebar-overlay" onClick={() => setIsSidebarOpen(false)} />}

      <ProductSidebar
        isOpen={isSidebarOpen}
        title="Add Product"
        product={newProduct}
        isSubmitting={isSubmitting}
        onClose={() => setIsSidebarOpen(false)}
        onSubmit={handleAddProduct}
        onChange={handleInputChange}
        onSelectImages={(urls: string[]) => setNewProduct((prev) => ({ ...prev, images: urls }))}
      />

      <ConfirmModal
        isOpen={showModal}
        title="Product verwijderen"
        message={`Weet je zeker dat je '${selectedFile?.title}' wilt verwijderen?`}
        onConfirm={confirmDeleteProduct}
        onCancel={() => {
          setShowModal(false);
          setSelectedFile(null);
        }}
      />
    </div>
  );
};

export default ProductManager;
